// Global Instructions Rule Applied!
//
// The Terms section of a commitment: its own management fee, carry and hurdle
// figures (rates typed as percentages; a blank management fee means the
// fund's rate, a blank carry term the fund's default carry term - both shown
// as placeholders). Shared by the Commitment form and the New commitment form.
// On a pocket of a consolidated commitment the terms have no editor, so the
// section is read-only there.

import React, { useMemo } from 'react'

import { FormSection } from '../../../core/components/FormSection.tsx'
import { InputManager } from '../../../core/components/InputManager/index.ts'
import type { FieldValue } from '../../../core/components/InputManager/types.ts'
import { decimalToPercent } from '../../../core/utils/inputNumberFormat.ts'
import type { FundCarryDefaults } from '../../../core/utils/fundCarryDefaults.ts'
import type { CommitmentTermsDraft } from '../utils/commitmentDrafts.ts'
import { getTermsFields } from '../utils/commitmentFieldConfigs.ts'

interface Props {
  terms: CommitmentTermsDraft
  /** the fund's management fee rate, a fraction (0.02) */
  fundFeeRate: number | null
  /** the fund's default carry terms (the Carried Interest section of its Terms tab); null = none stated */
  fundDefaults?: FundCarryDefaults | null
  onChange: (key: string, value: FieldValue) => void
  readOnly?: boolean
  /** bumped by the form to put the fields back (Cancel) */
  resetVersion?: number
}

export function CommitmentTermsSection({
  terms,
  fundFeeRate,
  fundDefaults = null,
  onChange,
  readOnly = false,
  resetVersion = 0
}: Props): React.ReactElement {
  const fields = useMemo(
    () => getTermsFields(terms, { fundFeeRatePct: decimalToPercent(fundFeeRate), fundDefaults, readOnly }),
    [terms, fundFeeRate, fundDefaults, readOnly]
  )
  return (
    <FormSection title='Terms' titleColor='blue' scrollable={false}>
      {readOnly ? (
        <InputManager inputMode='read' labelPosition='above' fields={fields} />
      ) : (
        <InputManager
          key={`termsFields-${resetVersion}`}
          inputMode='edit'
          labelPosition='above'
          fields={fields}
          onChange={onChange}
        />
      )}
    </FormSection>
  )
}

export default CommitmentTermsSection
